import { useState, useCallback } from "react";
import { createAipHistory } from "../utils/api";

const detectChangedFields = (previous = {}, current = {}) => {
  const changedFields = [];
  const fields = [
    "overall",
    "wellBeing",
    "careTeam",
    "environment",
    "vocalAnxiety",
    "vocalDepression",
    "vocalAggression",
    "vocalWellness",
  ];
  fields.forEach((field) => {
    if (previous[field] !== current[field]) {
      changedFields.push(field);
    }
  });
  return changedFields;
};

const calculateOverallScore = (scores = {}) => {
  if (scores.overall !== undefined && scores.overall !== null) {
    return Number(scores.overall);
  }
  const { wellBeing, careTeam, environment } = scores;
  if (
    wellBeing !== undefined &&
    careTeam !== undefined &&
    environment !== undefined
  ) {
    return Number(((Number(wellBeing) + Number(careTeam) + Number(environment)) / 3).toFixed(2));
  }
  return 0;
};

export const useAipHistoryTracker = (clientId) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const track = useCallback(
    async (previousScores, newScores, changedBy = "manual", reason = "") => {
      if (!clientId) {
        console.error("Cannot track score change: clientId is missing");
        return null;
      }
      setIsSaving(true);
      setError(null);
      try {
        const changedFields = detectChangedFields(previousScores || {}, newScores || {});
        if (changedFields.length === 0) {
          setIsSaving(false);
          return null;
        }
        const scoresWithOverall = {
          ...newScores,
          overall: calculateOverallScore(newScores || {}),
        };
        const payload = {
          clientId,
          scores: {
            overall: scoresWithOverall.overall,
            wellBeing: scoresWithOverall.wellBeing || 0,
            careTeam: scoresWithOverall.careTeam || 0,
            environment: scoresWithOverall.environment || 0,
            vocalAnxiety: scoresWithOverall.vocalAnxiety || 0,
            vocalDepression: scoresWithOverall.vocalDepression || 0,
            vocalAggression: scoresWithOverall.vocalAggression || 0,
            vocalWellness: scoresWithOverall.vocalWellness || 0,
          },
          reason:
            reason ||
            `${changedBy === "automatic" ? "Auto" : "Updated"}: ${changedFields.join(
              ", "
            )}`,
        };
        const saved = await createAipHistory(payload);
        setIsSaving(false);
        return saved;
      } catch (err) {
        console.error("Failed to create AIP history entry:", err);
        setError(err.message || "Failed to save history");
        setIsSaving(false);
        return null;
      }
    },
    [clientId]
  );

  const trackScoreChange = useCallback(
    (previousScores, newScores, reason = "") =>
      track(previousScores, newScores, "manual", reason),
    [track]
  );

  const trackAutomaticChange = useCallback(
    (previousScores, newScores, reason = "Automatic recalculation") =>
      track(previousScores, newScores, "automatic", reason),
    [track]
  );

  const trackInitialAssessment = useCallback(
    (scores, reason = "Initial assessment") =>
      track({}, scores, "manual", reason),
    [track]
  );

  return {
    trackScoreChange,
    trackAutomaticChange,
    trackInitialAssessment,
    isSaving,
    error,
  };
};

export default useAipHistoryTracker;

